// ─── Base API Client ───
// Fetch wrapper with auth token injection, JSON parsing, and error handling.

/// <reference types="vite/client" />

import type { ApiError as ApiErrorBody } from '@a8-planner/shared';
import { isFieldForbiddenProblem } from '@a8-planner/shared/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

// ─── Auth ───
// The session is carried SOLELY by the HttpOnly `a8planner_access_token` cookie (set server-side by
// POST /auth/callback and /auth/dev-login), which travels automatically thanks to
// `credentials: 'include'` on every request. There is deliberately NO client-readable token:
// the previous localStorage `a8planner_access_token` + `Authorization: Bearer` fallback (a Phase-3
// migration bridge) was RETIRED in Phase 10 — an XSS attacker can read localStorage but never an
// HttpOnly cookie, so keeping a token in JS was a token-theft surface with no remaining purpose
// (cookie auth already worked everywhere). Server callback responses still include a `token`
// field for now; the client simply ignores it.

// ─── ApiError class ───
export class ApiError extends Error {
  readonly status: number;
  readonly type: string;
  readonly detail: string;
  readonly instance: string;
  /**
   * The full parsed RFC 7807 problem body, including any problem-specific
   * extension members that don't appear on the base `ApiErrorBody` shape
   * (e.g. ADR 0018's `fields` on the field-forbidden 403). `raw` is the same
   * object `JSON.parse` produced — extension members survive at runtime even
   * though `ApiErrorBody`'s type doesn't name them. Consumers that need one
   * should narrow via a type guard, e.g. `isFieldForbiddenProblem(error.raw)`
   * (see lib/onFieldForbidden.ts).
   */
  readonly raw: unknown;

  constructor(body: ApiErrorBody) {
    // Surface the RFC 7807 `detail` (specific reason) as the Error message,
    // falling back to `title` (generic status name) only if detail is empty.
    // This is what propagates to UI surfaces like AuthCallback's `?error=...`,
    // so it must be informative — "Authorization code exchange failed" reads
    // very differently from a generic "Unauthorized".
    super(body.detail || body.title);
    this.name = 'ApiError';
    this.status = body.status;
    this.type = body.type;
    this.detail = body.detail;
    this.instance = body.instance;
    this.raw = body;
  }
}

// ─── Idempotency key generation ───
function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

// ─── Request options ───
// Extends RequestInit with client-only flags that must never reach `fetch()`.
export interface RequestOptions extends RequestInit {
  /**
   * NP-P65a: suppresses the global 401 → /login redirect for this single
   * call. Used by the auth bootstrap check (`AuthProvider.checkAuth`), which
   * must be able to probe `/auth/me` on a cold load without racing a hard
   * navigation if the cookie/token turns out to be absent or invalid.
   */
  suppressAuthRedirect?: boolean;
  /**
   * Batch 6 vacations UX arc (Slice 4, spec §2.7) — an explicit
   * client-generated `Idempotency-Key` to send instead of the
   * auto-generated one below. Additive/backward-compatible: omitted (the
   * overwhelming majority of call sites), behavior is byte-for-byte
   * unchanged — a fresh UUID per call. Exists for callers that must
   * generate the key ONCE per logical unit of work and REUSE it across
   * retries of that same unit (a flaky-connection retry of the same
   * `POST /vacations` call must not double-book the range) — a concern the
   * auto-generated-per-call default can't express, since every call
   * (including a retry) would otherwise mint a brand new key.
   */
  idempotencyKey?: string;
}

// ─── Core request function ───
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const { suppressAuthRedirect, idempotencyKey, ...init } = options ?? {};

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  // Auth travels via the HttpOnly `a8planner_access_token` cookie (`credentials: 'include'` below) —
  // no Authorization header is set client-side (the Bearer/localStorage fallback was retired, see
  // the header note above).

  // Auto-include Idempotency-Key on POST/PUT — reuses the caller-supplied
  // key when one was passed (see `RequestOptions.idempotencyKey`'s doc
  // comment), else mints a fresh one per call exactly as before.
  if (method === 'POST' || method === 'PUT') {
    headers['Idempotency-Key'] = idempotencyKey ?? generateIdempotencyKey();
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
    credentials: 'include',
    ...init,
    // Ensure headers from options don't override our computed headers
    // by placing them above and spreading options last for other fields
  });

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  // Parse JSON response
  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    // 401 → session expired or invalid — redirect to login. (Nothing to clear client-side; the
    // HttpOnly cookie is the only session state and the server expires it.)
    if (response.status === 401) {
      // NP-P65a: `suppressAuthRedirect` lets a single call (the auth
      // bootstrap probe) observe a 401 without triggering the hard nav below
      // — otherwise a cold load with no session would redirect-loop/flicker
      // before AuthProvider has even finished mounting.
      // Only redirect if not already on the login or auth callback pages.
      if (
        !suppressAuthRedirect &&
        !window.location.pathname.startsWith('/login') &&
        !window.location.pathname.startsWith('/auth')
      ) {
        // Preserve the deep-link destination so /login can send the user
        // back where they were heading once they re-authenticate.
        const destination = `${window.location.pathname}${window.location.search || ''}`;
        window.location.href = `/login?returnTo=${encodeURIComponent(destination)}`;
      }
    }

    // Try to parse as RFC 7807 Problem Details
    const errorBody = data as ApiErrorBody | null;
    if (errorBody && errorBody.title && errorBody.status) {
      throw new ApiError(errorBody);
    }
    // Fallback error
    throw new ApiError({
      type: 'about:blank',
      title: response.statusText || 'Request failed',
      status: response.status,
      detail:
        typeof data === 'object' && data !== null && 'message' in data
          ? String((data as { message: string }).message)
          : `HTTP ${response.status}`,
      instance: path,
    });
  }

  return data as T;
}

// ─── Public API Methods ───
export const apiClient = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>('GET', path, undefined, options);
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('POST', path, body, options);
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PATCH', path, body, options);
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('PUT', path, body, options);
  },

  delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>('DELETE', path, body, options);
  },
};

// ─── Completion-workflow error guards (Batch E slice 3, design spec §8/§15) ───
// Small siblings to the shared `isFieldForbiddenProblem` guard, kept HERE (not in
// packages/shared) because — unlike the field-forbidden 403 — neither of these needs a
// server-authored discriminant the api emitter and this client must never drift on; they
// only need to be told apart from ApiError's other 403/422 callers, which is a pure
// client-side concern (design spec §15's third open item).

/**
 * RFC 7807 `type` for `POST /tasks/{id}/complete`'s 422 (design spec §8, the
 * "validation-plan-required" gate re-check). ASSUMPTION flagged for Backend Dev
 * reconciliation: the frozen contract names this problem by slug but does not pin an
 * exact `type` URI, so this guard treats `status === 422` on this call's own error as the
 * authoritative signal (this endpoint has exactly one 422 case) and only uses `type` as a
 * best-effort corroboration when present — it never fails the match on `type` alone.
 */
export const VALIDATION_PLAN_REQUIRED_PROBLEM_TYPE =
  'https://a8planner/problems/validation-plan-required';

/** Type guard: is this RFC 7807 problem the `/complete` 422 (missing validation plan)? */
export function isValidationPlanRequiredProblem(problem: unknown): problem is ApiErrorBody {
  if (typeof problem !== 'object' || problem === null) return false;
  const p = problem as Record<string, unknown>;
  if (p['status'] !== 422) return false;
  return p['type'] === undefined || p['type'] === VALIDATION_PLAN_REQUIRED_PROBLEM_TYPE;
}

/**
 * Type guard: is `error` the PLAIN (fields-less) 403 `/complete` emits (design spec
 * §8.1) — distinguished from the field-forbidden 403 (`isFieldForbiddenProblem`, which
 * carries a `fields` extension member) by the ABSENCE of that member. Every other 403 in
 * the app is field-forbidden or a coarse capability 403 unrelated to this action; callers
 * of this guard are always inside a `/complete` mutation's `onError`, so that ambiguity
 * doesn't arise in practice.
 */
export function isForbiddenError(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.status !== 403) return false;
  return !isFieldForbiddenProblem(error.raw);
}
